import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useOpenTask } from '@/features/tasks/public';
import { useSubmission } from '../../hooks/useSubmission';
import { CreateTaskFromSubmissionDialog } from './CreateTaskFromSubmissionDialog';
import type { FormField } from '../../types';

interface SubmissionDetailDialogProps {
  formId: string;
  submissionId: string | null;
  fields: FormField[];
  canCreateTask: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SubmissionDetailDialog({
  formId,
  submissionId,
  fields,
  canCreateTask,
  onOpenChange,
}: SubmissionDetailDialogProps) {
  const { t } = useTranslation('forms');
  const openTask = useOpenTask();
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const { submission, isLoading } = useSubmission(formId, submissionId ?? '');

  return (
    <>
      <Dialog open={!!submissionId} onOpenChange={(v) => !v && onOpenChange(false)}>
        <DialogContent
          className="w-full h-full max-w-none rounded-none md:h-auto md:max-w-lg md:rounded-lg"
          data-testid="forms-submission-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>{t('submissions.detail.title')}</DialogTitle>
          </DialogHeader>

          {isLoading || !submission ? (
            <div className="space-y-3" aria-hidden="true">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {fields
                .slice()
                .sort((a, b) => a.order - b.order)
                .map((field) => {
                  const value = submission.values.find((v) => v.fieldId === field.id)?.value;
                  return (
                    <div key={field.id} className="space-y-1">
                      <p className="text-xs text-muted-foreground">{field.label}</p>
                      <p className="text-sm">{value || '—'}</p>
                    </div>
                  );
                })}

              <div className="pt-2 border-t">
                {submission.taskId ? (
                  <button
                    type="button"
                    onClick={() => openTask(submission.taskId as string)}
                    className="flex items-center gap-1 text-sm text-primary hover:underline"
                    data-testid="forms-submission-view-task-link"
                  >
                    {t('submissions.detail.viewTask')}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  canCreateTask && (
                    <Button
                      variant="outline"
                      onClick={() => setCreateTaskOpen(true)}
                      data-testid="forms-submission-create-task-button"
                    >
                      {t('submissions.detail.createTask')}
                    </Button>
                  )
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {submissionId && (
        <CreateTaskFromSubmissionDialog
          formId={formId}
          submissionId={submissionId}
          open={createTaskOpen}
          onOpenChange={setCreateTaskOpen}
        />
      )}
    </>
  );
}
